import { computeDesignContentHash } from "@cogo/design-data/design-mutation";

const APPROVAL_GRANT_TTL_MS = 60_000;
const APPROVAL_TOKEN_KEY = "_capabilityApprovalToken";

interface ApprovalGrant {
  tool: string;
  payloadHash: string;
  projectId: string | null;
  actorId: string;
  confirmationId: string;
  approvedAt: string;
  expiresAt: number;
}

const grants = new Map<string, ApprovalGrant>();

export function hashStudioCapabilityApprovalPayload(
  params: Record<string, unknown>,
): string {
  const payload = Object.fromEntries(
    Object.entries(params).filter(([key]) => key !== APPROVAL_TOKEN_KEY),
  );
  return computeDesignContentHash(JSON.parse(JSON.stringify(payload)));
}

export function issueStudioCapabilityApprovalGrant(input: {
  tool: string;
  params: Record<string, unknown>;
  projectId: string | null;
  actorId: string;
  confirmationId: string;
  approvedAt: string;
}): string {
  const now = Date.now();
  for (const [existingToken, grant] of grants) {
    if (grant.expiresAt <= now) grants.delete(existingToken);
  }
  const token = crypto.randomUUID();
  grants.set(token, {
    tool: input.tool,
    payloadHash: hashStudioCapabilityApprovalPayload(input.params),
    projectId: input.projectId,
    actorId: input.actorId,
    confirmationId: input.confirmationId,
    approvedAt: input.approvedAt,
    expiresAt: now + APPROVAL_GRANT_TTL_MS,
  });
  return token;
}

export function consumeStudioCapabilityApprovalGrant(input: {
  token: unknown;
  tool: string;
  params: Record<string, unknown>;
  projectId: string | null;
  failureScope?: "design-mutation" | "studio";
}): Pick<ApprovalGrant, "actorId" | "confirmationId" | "approvedAt"> {
  const failurePrefix =
    input.failureScope === "studio" ? "STUDIO" : "DESIGN_MUTATION";
  if (typeof input.token !== "string" || !input.token) {
    throw new Error(`${failurePrefix}_CAPABILITY_APPROVAL_REQUIRED`);
  }
  const grant = grants.get(input.token);
  grants.delete(input.token);
  if (
    !grant ||
    grant.expiresAt <= Date.now() ||
    grant.tool !== input.tool ||
    grant.projectId !== input.projectId ||
    grant.payloadHash !== hashStudioCapabilityApprovalPayload(input.params)
  ) {
    throw new Error(`${failurePrefix}_CAPABILITY_APPROVAL_INVALID`);
  }
  return {
    actorId: grant.actorId,
    confirmationId: grant.confirmationId,
    approvedAt: grant.approvedAt,
  };
}

export function clearStudioCapabilityApprovalGrantsForTests(): void {
  grants.clear();
}
